import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { observer } from 'mobx-react'
import { Button, Descriptions, Flex, Modal, Skeleton } from 'antd'
import { ArrowLeftOutlined, ExclamationCircleOutlined, LinkOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { StatusBadge, SourceBadge } from '@pushly/aqe/lib/components/badges'
import Well from '@pushly/aqe/lib/components/well/well'
import { useAppState, useMobileService } from '../hooks/use-mobile-service'
import { useNotificationDetail } from '../hooks/use-notification-detail'
import { useNotificationActions } from '../hooks/use-notification-actions'
import { useNotificationPermissions } from '../hooks/use-notification-permissions'
import { MobileEmptyState } from '../components/mobile-empty-state'
import { NotificationPreview } from '../components/notification-preview/notification-preview'
import { StatsRow } from '../components/stats-row/stats-row'
import { InsightsService } from '../../services/insights'
import { StatusType } from '../../enums/status-type'
import { NotificationDeliveryWindow } from '@pushly/models/lib/enums/notification-delivery-window'
import { NotificationSource } from '@pushly/models/lib/enums/notification-source'
import { ReportEntity } from '../../features/reporting_v2/enums/report-entity'
import { ReportDateIncrement } from '../../features/reporting_v2/enums/report-date-increment'
import { ReportDatePreset } from '../../features/reporting_v2/enums/report-date-preset'
import { isNotificationEditable } from '../../features/notifications/helpers/notification-helpers'
import { getDeliveryStrategyLabel, formatCompact } from '../notifications/shared/notification-constants'
import { NotificationActions } from '../notifications/shared/notification-actions'
import { ChannelList } from '../components/channel-list'
import { AudienceDisplay } from '../components/audience-display'
import { mobileRoutes } from '../utils/mobile-routes'
import './notification-detail.scss'

/** Validates a URL starts with http/https (prevents javascript: XSS) */
function isSafeUrl(url: string): boolean {
    return /^https?:\/\//i.test(url)
}

export const NotificationDetail: React.FC = observer(() => {
    const { domainId, notifId } = useParams<{ domainId: string; notifId: string }>()
    const appState = useAppState()
    const insightsService = useMobileService(InsightsService)
    const history = useHistory()

    const { data: notif, loading } = useNotificationDetail(domainId, notifId, {
        cancellationKey: 'mv.notif-detail.fetch',
    })
    const [stats, setStats] = useState<{ impressions?: number; clicks?: number; ctrDecimal?: number } | null>(null)
    const [statsLoading, setStatsLoading] = useState(false)

    const timezone = appState.currentDomain?.timezone ?? 'UTC'

    // Fetch stats for delivered notifications
    useEffect(() => {
        if (!notif) return

        const isDelivered = [StatusType.COMPLETED.name, StatusType.DELIVERING.name, 'COMPLETED_WITH_FAILURES'].includes(
            notif.status ?? '',
        )

        if (!isDelivered) return

        let cancelled = false
        setStatsLoading(true)

        const fetchStats = async () => {
            try {
                const result = await insightsService.fetch(
                    {
                        entity: ReportEntity.NOTIFICATIONS,
                        date_preset: ReportDatePreset.LIFETIME,
                        date_increment: ReportDateIncrement.LIFETIME,
                        breakdowns: ['notification'],
                        fields: ['notification.id', 'impressions', 'clicks', 'ctr_decimal'],
                        action_attribution: 'send_time',
                        filters: [
                            { field: 'domain.id', operator: 'eq', value: Number(domainId) },
                            { field: 'notification.id', operator: 'eq', value: Number(notifId) },
                        ],
                    },
                    { cancellationKey: 'mv.notif-detail.stats', scopedInsightsPath: 'insights' },
                )

                if (!cancelled && result && Array.isArray(result) && result.length > 0) {
                    const s = result[0]
                    setStats({
                        impressions: s.impressions ?? 0,
                        clicks: s.clicks ?? 0,
                        ctrDecimal: s.ctr_decimal ?? 0,
                    })
                }
            } catch {
                // Stats fetch failure is non-critical — detail page still renders without stats
            } finally {
                if (!cancelled) setStatsLoading(false)
            }
        }
        fetchStats()

        return () => {
            cancelled = true
        }
    }, [notif, domainId, notifId, insightsService])

    const handleBack = useCallback(() => {
        history.goBack()
    }, [history])

    const handleGoToDashboard = useCallback(() => {
        history.replace(mobileRoutes.dashboard(domainId))
    }, [history, domainId])

    const { handleEdit, handleDuplicate, handleCancel } = useNotificationActions({
        domainId,
        notificationId: notifId,
        title: notif?.template?.channels?.default?.title,
        scheduleId: notif?.schedules?.[0]?.id ?? notif?.id,
        onChange: handleBack,
        onAfterCancel: handleBack,
    })

    // Derived values
    const title = notif?.template?.channels?.default?.title ?? 'Untitled'
    const body = notif?.template?.channels?.default?.body
    const imageUrl = notif?.template?.channels?.default?.image_url
    const landingUrl = notif?.template?.channels?.default?.landing_url
    const channels: string[] = notif?.channels ?? []
    const subscriberActions: Array<{
        label: string
        type?: string
        landing_url?: string
        use_primary_landing_url?: boolean
    }> = (() => {
        const tmpl = notif?.template?.channels
        if (!tmpl) return []
        // Only check channel-specific actions if that channel is targeted
        if (channels.includes('WEB') && tmpl.web?.actions?.length) return tmpl.web.actions
        if (channels.includes('NATIVE_ANDROID') && tmpl.native_android?.actions?.length)
            return tmpl.native_android.actions
        return tmpl.default?.actions ?? []
    })()
    const keywords: string[] = notif?.template?.keywords ?? []
    const audience = notif?.audience
    const createdBy = notif?.created_by_user_name

    // Delivery Date Range (start through expiry based on TTL)
    const deliveryDateRange = useMemo(() => {
        const spec = notif?.delivery_spec
        if (!spec?.send_date_utc) return undefined

        const isSTZ = spec.window === NotificationDeliveryWindow.TIMEZONE
        const format = isSTZ ? 'MMM D, YYYY h:mm A [STZ]' : 'MMM D, YYYY h:mm A z'

        const startDate = dayjs.utc(spec.send_date_utc).tz(timezone)
        const start = startDate.format(format)

        if (spec.ttl_seconds) {
            const endDate = startDate.add(spec.ttl_seconds, 'seconds')
            const end = endDate.format(format)
            return { start, end }
        }

        return { start, end: undefined }
    }, [notif, timezone])

    const deliveryStrategy = useMemo(() => (notif ? getDeliveryStrategyLabel(notif) : undefined), [notif])

    const contentSource = useMemo(() => {
        const results = notif?.template?.channels?.default?.auto_suggested_fields_results as
            | {
                  source?: string
                  source_metadata?: { tone?: string; reason?: string }
                  sourceMetadata?: { tone?: string; reason?: string }
                  edited?: boolean
                  title?: string
                  body?: string
                  imageUrl?: string
              }
            | null
            | undefined
        if (!results) return undefined

        let source = results.source
        if (!source && (results.title || results.body || results.imageUrl)) source = 'page_analyzer'
        if (!source) return undefined

        const metadata = results.source_metadata ?? results.sourceMetadata
        const tone = metadata?.tone
        const reason = metadata?.reason
        const baseName = source === 'pushly_ai' ? 'Pushly AI' : 'Page Analyzer'
        const displayName = results.edited ? `${baseName} (Edited)` : baseName

        return { source, displayName, tone, reason }
    }, [notif])

    // Audience split: targeted vs excluded
    const targetedSegments = audience?.segments ?? []
    const excludedSegments = audience?.excluded_segments ?? []

    // Permissions
    const { canCreate, canUpdate } = useNotificationPermissions()
    const isScheduled = notif?.status === StatusType.SCHEDULED.name
    const editable = notif && isNotificationEditable(notif)

    // Stats display values
    const statsDisplay = useMemo(() => {
        if (!stats) return null
        const ctrPct = stats.ctrDecimal != null && stats.ctrDecimal > 0 ? stats.ctrDecimal * 100 : 0
        // Drop unnecessary decimals for cleaner display in tight spaces
        const ctrFormatted = ctrPct > 0 ? (ctrPct % 1 === 0 ? `${ctrPct.toFixed(0)}%` : `${ctrPct.toFixed(2)}%`) : '--'
        return {
            impressions: formatCompact(stats.impressions ?? 0),
            clicks: formatCompact(stats.clicks ?? 0),
            ctr: ctrFormatted,
        }
    }, [stats])

    if (loading) {
        return (
            <div className="notification-detail">
                <div className="notification-detail__back">
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={handleBack}
                        className="notification-detail__back-btn"
                    >
                        Back
                    </Button>
                </div>
                <Skeleton active paragraph={{ rows: 8 }} />
            </div>
        )
    }

    if (!notif) {
        return (
            <Flex vertical gap="var(--space-3)" className="notification-detail">
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={handleBack}
                    className="notification-detail__back-btn"
                    style={{ alignSelf: 'flex-start' }}
                >
                    Back
                </Button>
                <MobileEmptyState
                    icon={<ExclamationCircleOutlined />}
                    description="Notification not found"
                    action={{ label: 'Go to Dashboard', onClick: handleGoToDashboard }}
                />
            </Flex>
        )
    }

    return (
        <Flex vertical gap="var(--space-3)" className="notification-detail">
            {/* Back button — page-level navigation, above wells */}
            <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={handleBack}
                className="notification-detail__back-btn"
                style={{ alignSelf: 'flex-start' }}
            >
                Back
            </Button>

            {/* Notification Preview — cuttlefish device mockup */}
            {/* <NotificationPreview
                appName={appState.currentDomain?.displayName ?? appState.currentDomain?.name ?? ''}
                title={title}
                body={body}
                imageUrl={imageUrl}
                iconUrl={notif?.template?.channels?.default?.icon_url ?? appState.currentDomain?.defaultIconUrl}
                actions={subscriberActions.length > 0 ? subscriberActions.map((a) => ({ label: a.label || 'Open' })) : undefined}
            /> */}

            {/* Content Well */}
            <Well title="Notification Content" padding="small" hideFooter>
                <div className="notification-detail__title">{title}</div>
                {body && <p className="notification-detail__body">{body}</p>}
                {imageUrl && <img className="notification-detail__image" src={imageUrl} alt="" loading="lazy" />}
                {landingUrl && isSafeUrl(landingUrl) && (
                    <a
                        href="#"
                        className="notification-detail__url"
                        onClick={(e) => {
                            e.preventDefault()
                            Modal.confirm({
                                title: 'Open Link',
                                icon: <LinkOutlined style={{ color: 'var(--color-secondary-100)' }} />,
                                content: (
                                    <span>
                                        This will open the following destination in a new window:
                                        <br />
                                        <strong style={{ wordBreak: 'break-all' }}>{landingUrl}</strong>
                                    </span>
                                ),
                                okText: 'Open',
                                cancelText: 'Cancel',
                                onOk: () => {
                                    window.open(landingUrl, '_blank', 'noopener,noreferrer')
                                },
                            })
                        }}
                    >
                        {landingUrl}
                    </a>
                )}
                {subscriberActions.length > 0 && (
                    <Flex gap="var(--space-2)" wrap="wrap" className="notification-detail__subscriber-actions">
                        {subscriberActions.map((action, idx) => {
                            const isCloseAction = action.type === 'CLOSE'
                            const resolvedUrl =
                                !isCloseAction && action.use_primary_landing_url
                                    ? landingUrl
                                    : !isCloseAction
                                    ? action.landing_url
                                    : undefined
                            const hasUrl = resolvedUrl && isSafeUrl(resolvedUrl)
                            return (
                                <Button
                                    key={idx}
                                    size="small"
                                    style={{ flex: 1, maxWidth: 200 }}
                                    onClick={() => {
                                        if (isCloseAction) {
                                            Modal.info({
                                                title: 'Dismiss Action',
                                                content: 'This action dismisses the notification for the subscriber.',
                                                okText: 'Close',
                                            })
                                            return
                                        }
                                        if (!hasUrl) return
                                        Modal.confirm({
                                            title: 'Open Link',
                                            icon: <LinkOutlined style={{ color: 'var(--color-secondary-100)' }} />,
                                            content: (
                                                <span>
                                                    This will open the following destination in a new window:
                                                    <br />
                                                    <strong style={{ wordBreak: 'break-all' }}>{resolvedUrl}</strong>
                                                </span>
                                            ),
                                            okText: 'Open',
                                            cancelText: 'Cancel',
                                            onOk: () => {
                                                window.open(resolvedUrl!, '_blank', 'noopener,noreferrer')
                                            },
                                        })
                                    }}
                                >
                                    {action.label || (isCloseAction ? 'Dismiss' : 'Open')}
                                </Button>
                            )
                        })}
                    </Flex>
                )}
                {(landingUrl || subscriberActions.length > 0) && (
                    <p className="notification-detail__subscriber-hint">
                        Tap {subscriberActions.length > 1 ? 'buttons' : 'button'} to inspect subscriber-facing actions.
                    </p>
                )}
            </Well>

            {/* Performance Stats — no well, cards provide their own elevation */}
            {(statsLoading || statsDisplay) && (
                <StatsRow
                    loading={statsLoading && !statsDisplay}
                    impressions={statsDisplay?.impressions ?? { display: '--' }}
                    clicks={statsDisplay?.clicks ?? { display: '--' }}
                    ctr={statsDisplay?.ctr ?? '--'}
                />
            )}

            {/* Details Well */}
            <Well title="Details" padding="small" hideFooter>
                <Descriptions
                    column={1}
                    size="small"
                    colon={false}
                    labelStyle={{ width: 130, color: 'var(--color-gray-dark)' }}
                    contentStyle={{ fontWeight: 500 }}
                >
                    <Descriptions.Item label="ID">#{notif.id}</Descriptions.Item>
                    {createdBy && <Descriptions.Item label="Created By">{createdBy}</Descriptions.Item>}
                    <Descriptions.Item label="Status">
                        <StatusBadge type="notification" status={notif.status ?? ''} expanded />
                    </Descriptions.Item>
                    {notif.source && notif.source !== NotificationSource.MANUAL && (
                        <Descriptions.Item label="Source">
                            <SourceBadge type="notification" source={notif.source} expanded />
                        </Descriptions.Item>
                    )}
                    {deliveryDateRange && (
                        <Descriptions.Item label="Delivery Date Range">
                            <span>
                                {deliveryDateRange.start}
                                {deliveryDateRange.end && (
                                    <>
                                        <span className="notification-detail__through"> through</span>
                                        <br />
                                        {deliveryDateRange.end}
                                    </>
                                )}
                            </span>
                        </Descriptions.Item>
                    )}
                    {deliveryStrategy && (
                        <Descriptions.Item label="Delivery Strategy">{deliveryStrategy}</Descriptions.Item>
                    )}
                    {channels.length > 0 && (
                        <Descriptions.Item label="Channels">
                            <ChannelList channels={channels} />
                        </Descriptions.Item>
                    )}
                    {(targetedSegments.length > 0 || audience?.all_subscribers) && (
                        <Descriptions.Item label="Targeted Audience(s)">
                            <AudienceDisplay allSubscribers={audience?.all_subscribers} segments={targetedSegments} />
                        </Descriptions.Item>
                    )}
                    {excludedSegments.length > 0 && (
                        <Descriptions.Item label="Excluded Audience(s)">
                            <AudienceDisplay segments={excludedSegments} />
                        </Descriptions.Item>
                    )}
                    {keywords.length > 0 && (
                        <Descriptions.Item label="Keywords">{keywords.join(', ')}</Descriptions.Item>
                    )}
                    {contentSource && (
                        <Descriptions.Item label="Content Source">{contentSource.displayName}</Descriptions.Item>
                    )}
                    {contentSource?.tone && (
                        <Descriptions.Item label="AI Content Tone">
                            {contentSource.tone.charAt(0).toUpperCase() + contentSource.tone.slice(1)}
                        </Descriptions.Item>
                    )}
                    {contentSource?.reason && (
                        <Descriptions.Item label="AI Content Reason">{contentSource.reason}</Descriptions.Item>
                    )}
                </Descriptions>
            </Well>

            {/* Actions — no well, page-level CTAs */}
            <NotificationActions
                className="notification-detail__actions"
                canEdit={!!(editable && isScheduled && canUpdate)}
                canDuplicate={canCreate}
                canCancel={!!(isScheduled && canUpdate)}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onCancel={handleCancel}
            />
        </Flex>
    )
})

NotificationDetail.displayName = 'NotificationDetail'
